import {
  LegacyProductCode,
  ProductBehavior,
} from '@up2stay/up2promo-api-client';

import { shellPaths } from '@/core/router/app-paths';
import { parseSetupTypeParam } from '@/core/lib/product-setup-type';

export type ProductsSetupPathOptions = {
  hash: string;
  /** Prefer api-client enum; plain codes are parsed. */
  productType?: LegacyProductCode | string | null;
  /**
   * Defaults to workspace products setup. Use `profilesLinkCard` for BC
   * link-card entry.
   */
  basePath?: string;
};

export type LinkResolveSetupPathOptions = {
  hash: string;
  productType?: LegacyProductCode | string | null;
  mappingType?: ProductBehavior | string | null;
  /** Classified app path from resolve (may already be setup / link-card). */
  appPath?: string | null;
  /**
   * When true, BC deep-links use workspace products setup (profile owner
   * picker). When false, BC stays on Profiles link-card (self-only).
   * Mirrors `PRODUCTS_MANAGE` + backend products:manage assign rules.
   */
  canManageProducts: boolean;
};

function isBusinessCardSetupTarget(
  productType?: LegacyProductCode | string | null,
  mappingType?: ProductBehavior | string | null,
): boolean {
  return (
    productType === LegacyProductCode.BC ||
    mappingType === ProductBehavior.PROFILE
  );
}

function pathIsProfilesLinkCard(path: string): boolean {
  return path.includes('/profiles/link-card');
}

function pathIsProductsSetup(path: string): boolean {
  return path.includes('/products/setup');
}

/**
 * Build products setup (or link-card) URL with `hash` + setup-frontend `t=`.
 * Always prefer `t` over legacy `type` so deep links and refresh stay typed.
 */
export function buildProductsSetupPath({
  hash,
  productType,
  basePath = shellPaths.moreProductsSetup,
}: ProductsSetupPathOptions): string {
  const trimmed = hash.trim();
  const params = new URLSearchParams();
  if (trimmed) params.set('hash', trimmed);
  const type = parseSetupTypeParam(
    productType == null ? undefined : String(productType),
  );
  if (type) {
    params.set('t', type);
    params.delete('type');
  }
  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Deep-link / scan “continue setup” destination.
 *
 * Profiles → Link card stays self-only (`profilesLinkCard`). Managers who
 * reach setup via deep link / NFC / QR must use workspace products setup so
 * they can assign the card to another member. Members without
 * `PRODUCTS_MANAGE` keep the self-only link-card path (route + API both
 * enforce self-assign).
 */
export function resolveLinkResolveSetupPath({
  hash,
  productType,
  mappingType,
  appPath,
  canManageProducts,
}: LinkResolveSetupPathOptions): string {
  const isBc = isBusinessCardSetupTarget(productType, mappingType);
  const useWorkspaceSetup = !isBc || canManageProducts;
  const basePath = useWorkspaceSetup
    ? shellPaths.moreProductsSetup
    : shellPaths.profilesLinkCard;

  if (appPath?.trim()) {
    const path = appPath.trim();
    if (useWorkspaceSetup && pathIsProfilesLinkCard(path)) {
      return buildProductsSetupPath({
        hash: hashFromSetupPath(path, hash),
        productType:
          mergeSetupProductType(productType, typeFromSetupPath(path)) ??
          productType,
        basePath: shellPaths.moreProductsSetup,
      });
    }
    if (!useWorkspaceSetup && pathIsProductsSetup(path)) {
      return buildProductsSetupPath({
        hash: hashFromSetupPath(path, hash),
        productType:
          mergeSetupProductType(productType, typeFromSetupPath(path)) ??
          productType,
        basePath: shellPaths.profilesLinkCard,
      });
    }
    return ensureSetupPathHasProductType(path, productType);
  }

  return buildProductsSetupPath({
    hash,
    productType,
    basePath,
  });
}

function hashFromSetupPath(path: string, fallback: string): string {
  try {
    const url = new URL(path, 'https://app.local');
    return url.searchParams.get('hash')?.trim() || fallback.trim();
  } catch {
    return fallback.trim();
  }
}

function typeFromSetupPath(path: string): LegacyProductCode | undefined {
  try {
    const url = new URL(path, 'https://app.local');
    return parseSetupTypeParam(
      url.searchParams.get('t'),
      url.searchParams.get('type'),
    );
  } catch {
    return undefined;
  }
}

/**
 * Inject `t=` onto an existing setup / link-card path when missing.
 * Does not overwrite an already-valid `t` / `type`.
 */
export function ensureSetupPathHasProductType(
  path: string,
  productType?: LegacyProductCode | string | null,
): string {
  const type = parseSetupTypeParam(
    productType == null ? undefined : String(productType),
  );
  if (!type) return path;

  try {
    const url = new URL(path, 'https://app.local');
    const existing = parseSetupTypeParam(
      url.searchParams.get('t'),
      url.searchParams.get('type'),
    );
    if (existing) return path;
    url.searchParams.set('t', type);
    url.searchParams.delete('type');
    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  } catch {
    return path;
  }
}

/**
 * Merge product type candidates: first valid enum wins.
 * Call with DB / ownership first, then resolution, then QR `t` hint so a known
 * DB type wins without clearing a QR type when DB has none.
 */
export function mergeSetupProductType(
  ...candidates: Array<LegacyProductCode | string | null | undefined>
): LegacyProductCode | undefined {
  return parseSetupTypeParam(
    ...candidates.map((c) => (c == null ? undefined : String(c))),
  );
}
